// Tipos da gamificação: o resultado que a mesa registra no fim de cada partida, o que cada jogador ganhou
// e o armazém (memória, PostgreSQL ou, no desenvolvimento, a API local por HTTP).
import type { ProximaAcao } from './fim.ts';
import type { MissaoFechada } from './missoes.ts';
import type { OfensivaNoFim, ValoresOfensiva } from './ofensiva.ts';
import type { PasseNoFim, ValoresPasse } from './passe.ts';
import type { RodagemNoFim, ValoresRecompensa } from './recompensas.ts';
import type { Plano } from '@truco6/planos';
import type { Team } from '@truco6/regras';
import type { EstadoDivisao } from './divisoes.ts';
import type { Lance } from './lances.ts';
import type { NumerosDaPartida } from './numeros.ts';
import type { Periodo } from './tempo.ts';

export type TipoSala = 'liga' | 'amigo' | 'tentos' | 'campeonato'; // todas pontuam no ranking e no PD, com o mínimo de pessoas

export interface JogadorDaPartida {
  usuario: string;
  time: Team;
  saiu: boolean; // saiu da partida (caiu e não voltou não conta)
  lances: Lance[];
  numeros?: NumerosDaPartida; // truco, mão de onze, mão de ferro e Zap, para as estatísticas completas (a mesa grava desde o lote 6)
}

export interface ResultadoPartida {
  id: string; // SALA-criadaEm:partida, único
  codigo: string;
  tipo: TipoSala;
  privada: boolean;
  dono: string | null; // null na liga
  iniciadaEm: number;
  terminadaEm: number;
  vencedor: Team;
  placar: [number, number];
  jogadores: JogadorDaPartida[]; // só pessoas
  bots: [number, number]; // lugares de cada time que tiveram bot na partida (vaga ou quem saiu ou caiu)
  // Lugares que começaram a partida com bot (0 a 6): quem cai ou sai depois segue contando como pessoa. Sem o dado,
  // vale a soma de bots.
  botsNoInicio?: number;
  trios: [string | null, string | null]; // trio fixo de cada time, quando os 3 entraram juntos na busca
  liga: string | null; // id da liga, nas partidas da liga
  nome?: string; // nome da sala, para o retrospecto (só o de sala pública fica guardado)
  comunidade?: string | null; // mesa de uma comunidade: entra no ranking interno dela
  campeonato?: string | null; // sala de campeonato (o id do campeonato)
}

export type Desfecho = 'vitoria' | 'vitoria_contra_bot' | 'derrota' | 'saiu';

// Configuração em dados (tabela gamificacao_valores, com seed por migração). Mudar um valor não exige deploy.
export interface ValoresLiga {
  ranking_minimo_pessoas: number; // pessoas sentadas no início para a partida pontuar no ranking e no PD
  liga_vitoria: number;
  liga_vitoria_contra_bot: number;
  liga_derrota: number;
  liga_saiu: number;
  pd_vitoria: number;
  pd_derrota: number;
  pd_saiu: number;
}
export type Valores = ValoresLiga & ValoresRecompensa & ValoresOfensiva & ValoresPasse & Record<string, number>;

export const VALORES_PADRAO: ValoresLiga = {
  ranking_minimo_pessoas: 4,
  liga_vitoria: 30, liga_vitoria_contra_bot: 15, liga_derrota: 10, liga_saiu: -15,
  pd_vitoria: 25, pd_derrota: -15, pd_saiu: -30,
};

export const NIVEIS_DE_BADGE = ['bronze', 'prata', 'ouro', 'diamante'] as const;
export type NivelBadge = (typeof NIVEIS_DE_BADGE)[number];

export const CATEGORIAS_DE_BADGE = ['partidas', 'vitorias', 'lances', 'social', 'salas', 'ofensiva', 'temporada', 'divisao', 'regional', 'patrocinado', 'campeonatos'] as const;
export type CategoriaBadge = (typeof CATEGORIAS_DE_BADGE)[number];

export interface Badge {
  id: string;
  categoria: CategoriaBadge;
  nome: string;
  emoji: string;
  nivel: NivelBadge;
  contador: string; // a estatística do jogador que o badge mede
  meta: number;
  descricao: string;
  ordem: number;
}

export interface BadgeGanho { badge: string; nivel: NivelBadge; nome: string; emoji: string; em: number }

export interface Liga {
  id: string; // 'padrao' sem patrocinador
  nome: string;
  marca: { nome: string; logo: string | null; cor: string | null } | null; // null: sem patrocinador, sem telas de marca
  premio: string | null;
  semana: string;
  inicio: number;
  fim: number;
}

export interface GanhosDaPartida {
  partida: string;
  liga: {
    pontos: number;
    pd: number; // pontos de divisão da partida (antes do piso da divisão)
    desfecho: Desfecho;
    divisao: { antes: EstadoDivisao; depois: EstadoDivisao; subiu: boolean };
    semPontos: boolean; // a mesma conta numa outra partida ao mesmo tempo: não pontua
  } | null;
  trio: { id: string; pontos: number } | null;
  badges: BadgeGanho[];
  rodagem: RodagemNoFim | null; // null: saiu da partida (não terminou)
  ofensiva: OfensivaNoFim | null; // null: saiu da partida (não terminou)
  missoes: MissaoFechada[]; // missões que a partida fechou
  passe: PasseNoFim | null; // estrelas do Passe da Temporada; null: saiu da partida (não terminou)
  proxima: ProximaAcao | null; // a próxima ação em destaque (a revanche, a mesa decide); null: saiu da partida
  semRanking?: true; // a partida começou com menos pessoas que o mínimo (3 bots ou mais): sem ranking e sem PD
}

export interface Situacao {
  divisao: EstadoDivisao;
  temporada: string;
  pontos: Record<Periodo, number>;
}

// Rei do Pote: pontos é o saldo de mesa, vitorias são as partidas em que recebeu o prêmio e, só em eu, faltaTop10 é o
// saldo que falta para alcançar o 10º (null: já está no top 10). posicao 0: fora da lista (saldo zero ou negativo).
export interface LinhaRanking {
  posicao: number; id: string; nome: string; avatar: string | null; pontos: number; partidas: number; local: string | null;
  vitorias?: number; faltaTop10?: number | null;
  coroa?: boolean; // o 1º do Rei do Pote da semana passada (cidade, estado ou Brasil): a coroa sobre o rosto
  escudo?: string; membros?: string[]; // ranking de trios: o escudo e os apelidos dos 3
  jogadores?: { id: string; apelido: string; avatar: string | null }[]; // ranking de trios: os 3, para o botão de amizade
}
export interface Ranking { linhas: LinhaRanking[]; eu: LinhaRanking | null; atualizadoEm: number }
export type EscopoRanking = 'cidade' | 'estado' | 'brasil';
export type TipoRanking = 'jogadores' | 'trios' | 'pote';

// Plano e limite diário: no banco são funções (plano_de, situacao_limite, consumir_partida, devolver_partida);
// na memória, quem cria o armazém liga no repositório da API.
export type ConsumoDePartida = 'ilimitado' | 'diaria' | 'bonus' | 'sem_partidas';
export interface Contas {
  planoDe(usuario: string): Promise<Plano>;
  restantes(usuario: string): Promise<number | null>; // partidas grátis que faltam hoje, em qualquer mesa; null é ilimitado
  // Uma partida que começa, pela referência da partida (sala:número): a mesma referência não conta duas vezes.
  consumir(usuario: string, referencia: string): Promise<ConsumoDePartida>;
  // Desfaz o consumo daquela referência (partida anulada antes do fim da 1ª mão); true quando havia o que devolver.
  devolver?(usuario: string, referencia: string): Promise<boolean>;
  // Quem convidou esta conta (indique e ganhe), para o convite que paga quando o amigo joga. Na produção, a tabela
  // indicacoes; na memória, o repositório da API local.
  indicadorDe?(usuario: string): Promise<string | null>;
}
